import type { CompanyInfo } from "./clientTypes";

export const allOrganizationRoles = ["Org_Owner", "Org_Member", "Org_ExternalUser"] as const;
export type OrganizationRole = (typeof allOrganizationRoles)[number];

export type MemberRole =
  | "AP_Viewer"
  | "AP_ExternalUser"
  | "AP_Admin"
  | "RP_Viewer"
  | "RP_Maker"
  | "RP_Admin"
  | "FundOps_Member"
  | "FundOps_Admin"
  | "FundOps_Viewer"
  | "FundOps_IR_Admin"
  | "CorpAccounting_Viewer"
  | "CorpAccounting_Admin"
  | "Company_Settings_Viewer"
  | "Company_Settings_Admin"
  | "ExpenseMng_Member"
  | "ExpenseMng_Admin"
  | "Portfolio_Monitoring_Member"
  | "Portfolio_Monitoring_Submitter"
  | "Portfolio_Monitoring_Admin"
  | "Dashboard_Viewer"
  | "Dashboard_Admin";

export type AccessRole = OrganizationRole | MemberRole;

export const allClientUserTypes = ["External", "Internal", "Support"] as const;
export type ClientUserType = (typeof allClientUserTypes)[number];

export interface OrganizationMembersData {
  users: OrganizationMember[];
}

export interface OrganizationMember {
  userId: string;
  name: string;
  email: string;
  userType: ClientUserType;
  hasAccessToAllClients: boolean;
  organizationClientCode: string;
  organizationName: string;
  organizationPermissions: {
    roles: AccessRole[];
  };
  organizationContactRoles: OrganizationContactRole[];
  clientsAccess: ClientsAccess[];
  lastActivity?: string;
  firstAppearanceDate?: string;
  accessToDeveloperPortal: string[];
}

export interface ClientsAccess {
  clientCode: string;
  clientName: string;
  clientPermissions: {
    roles: MemberRole[];
    entitiesAccess?: string[];
    allowAccessToAllEntities: boolean;
  };
}

export type OrganizationContactRole =
  "PrimaryAccountContact" | "AccountManager" | "BillingContact" | "SecurityContact" | "TechnicalAdmin";

export interface OrganizationContact {
  userId?: string;
  name: string;
  email: string;
  roles: OrganizationContactRole[];
}

export interface UpdateOrganizationContactsRequest {
  organizationUserContacts: OrganizationUserContactInput[];
  externalContacts: ExternalContactInput[];
}

export interface OrganizationUserContactInput {
  userId: string;
  roles: OrganizationContactRole[];
}

export interface ExternalContactInput {
  name: string;
  email: string;
  roles: OrganizationContactRole[];
}

export interface OrganizationDeactivatedMembersData {
  deactivatedUsers: OrganizationDeactivatedMember[];
}

export interface OrganizationDeactivatedMember {
  userId: string;
  name: string;
  email: string;
  disabledAt: string;
}

export interface OrganizationMemberInvitation {
  invitationId: string;
  email: string;
  isGuest: boolean;
  organizationRole: OrganizationRole;
  invitedAt: string;
  invitationStatus: InvitationStatus;
}

export type InvitationStatus = "PendingApproval" | "Sent" | "Redeemed" | "Rejected";

export interface OrganizationMembersInvitationData {
  invitations: OrganizationMemberInvitation[];
}

export interface CompanyChange {
  clientCode: string;
  roles: MemberRole[];
}

export interface UpdateOrganizationUserAccessRequest {
  newOrganizationRole?: OrganizationRole;
  accessChanges: CompanyChange[];
  entitiesAccess: Record<string, EntitiesAccessConfig>;
}

export interface EntitiesAccessConfig {
  allowAccessToAllEntities: boolean;
  allowedEntityIds: string[];
}

export interface CreateInvitationRequest {
  organizationRole: OrganizationRole;
  emails: string[];
  roles: ClientRoles[];
  entitiesAccess: Record<string, EntitiesAccessConfig>;
}

interface ClientRoles {
  clientCode: string;
  roles: MemberRole[];
}

export interface CompaniesResponse {
  companies: Company[];
}

export interface CompaniesEntitiesResponse {
  companies: CompanyEntities[];
}

export interface CompanyEntities {
  clientCode: string;
  entities: AccessLevelEntity[];
}

export interface Company extends CompanyInfo {
  availableApplications: AppCodes[];
  accountingSettings?: CompanyAccountingSettings;
}

export interface AccessLevelEntity {
  id: string;
  name: string;
  no?: string;
}

export type AppCodes =
  | "BC"
  | "InvestorRelations"
  | "InvestorPortal"
  | "DWH"
  | "ApiGateway"
  | "SF"
  | "Plaid"
  | "Yodlee"
  | "ExpenseManagement"
  | "PortfolioMonitoring";

interface CompanyAccountingSettings {
  fundAccountingEnabled: boolean;
  corporateAccountingEnabled: boolean;
}

export interface ClientUserGroupBase {
  id: string;
  name: string;
}

export interface ClientUserGroupInfo extends ClientUserGroupBase {
  countOfMembers: number;
}

export interface ClientUserGroupDetails extends ClientUserGroupBase {
  members: ClientUserGroupMember[];
}

export interface ClientUserGroupMember {
  id: string;
  name: string;
  email: string;
  hasAccessToAllClients: boolean;
  access: MemberClientAccess[];
}

export interface MemberClientAccess {
  clientCode: string;
  clientName: string;
}

export interface CreateClientUserGroupRequest {
  name: string;
  memberUserIds: string[];
}

export interface UpdateClientUserGroupRequest {
  name?: string;
  memberUserIdsToAdd?: string[];
  memberUserIdsToRemove?: string[];
}

export interface DeleteUserGroupResponse {
  isDeleted: boolean;
  usedInWorkflows: DeleteUserGroupResponseWorkflow[];
}

export interface DeleteUserGroupResponseWorkflow {
  id: string;
  name: string;
  clientCode: string;
  clientName: string;
}
